import { toBigInt } from '@morpho-org/dapp-core'
import { formatters } from '@morpho-org/ui-v6/utils'

import type { ApiMarketWarning, ApiVaultWarning } from '@/helpers/warningTypes'

import {
  BAD_DEBT_UNREALIZED_VAULT_MESSAGE,
  MarketWarningType,
  ORACLE_PRICE_DERIVATION_MARKET_MESSAGE,
  ORACLE_PRICE_DERIVATION_VAULT_MESSAGE,
  ORACLE_UNUSABLE_MARKET_MESSAGE,
  ORACLE_UNUSABLE_VAULT_MESSAGE,
  SUSTAINED_LOW_LIQUIDITY_MARKET_MESSAGE,
  VaultV2WarningType,
  VaultWarningType
} from './warnings'

export const getMarketWarningMessage = (
  warning: ApiMarketWarning,
  loanAsset?: { symbol?: string; decimals: number }
): string | null => {
  switch (warning.type) {
    case MarketWarningType.BAD_DEBT_UNREALIZED: {
      const badDebtUsd = warning.metadata?.badDebtUsd
      const badDebtAssets = warning.metadata?.badDebtAssets
      if (badDebtAssets && loanAsset?.symbol && badDebtUsd) {
        const formattedBadDebt = formatters.short
          .unit(loanAsset.symbol)
          .digits(2)
          .of(toBigInt(badDebtAssets), loanAsset.decimals)
        const formattedBadDebtInUsd = formatters.short.unit('$').digits(2).of(badDebtUsd)
        return `Unrealized bad debt: ${formattedBadDebt} (${formattedBadDebtInUsd})`
      }
      if (badDebtUsd) {
        const formatted = formatters.short.unit('$').digits(2).of(badDebtUsd)
        return `Unrealized bad debt (${formatted})`
      }
      return 'Unrealized bad debt detected'
    }

    case MarketWarningType.NOT_WHITELISTED:
      return 'Market is not listed'

    case MarketWarningType.ORACLE_PRICE_DERIVATION:
      return ORACLE_PRICE_DERIVATION_MARKET_MESSAGE

    case MarketWarningType.ORACLE_UNUSABLE:
      return ORACLE_UNUSABLE_MARKET_MESSAGE

    case MarketWarningType.SUSTAINED_LOW_LIQUIDITY:
      return SUSTAINED_LOW_LIQUIDITY_MARKET_MESSAGE

    case MarketWarningType.CUSTOM:
      return warning.metadata?.content ?? null

    default:
      return null
  }
}

/**
 * `deprecated` is how `user.earnPositions` reports a listing history; the consumer shape carries
 * the history itself in `metadata.history`.
 */
export const getVaultWarningMessage = (
  warning: ApiVaultWarning & { deprecated?: boolean }
): string | null => {
  // Market warnings never surface on vault surfaces. The BFF still aggregates
  // them into `vault.warnings`, so drop them here rather than mislabelling them.
  if (warning.source === 'market') return null

  return getVaultWarningTypeMessage(warning.type, {
    delisted: warning.deprecated === true || (warning.metadata?.history?.length ?? 0) > 0,
    content: warning.metadata?.content ?? null
  })
}

/**
 * The message for a vault's own warning. `delisted` tells a deprecated vault from one that was
 * never listed; `content` is the text of a custom warning.
 */
export const getVaultWarningTypeMessage = (
  type: string,
  { delisted, content }: { delisted: boolean; content: string | null }
): string | null => {
  switch (type) {
    case VaultWarningType.NOT_WHITELISTED:
      return delisted ? 'This vault is deprecated.' : 'Vault is not listed'

    case VaultWarningType.SHORT_TIMELOCK:
      return 'This vault has a timelock below 3 days'

    // `deposit_disabled` is vault state, not a risk warning, so it produces no message
    // (falls through to `null`). It is surfaced via the disabled deposit button + tooltip.

    case VaultV2WarningType.TIMELOCK:
      return "Some of the vault's timelocks are set below the minimum required duration"

    // Same string value in VaultWarningType and VaultV2WarningType — one case covers both.
    case VaultWarningType.LOW_LIQUIDITY:
      return "Less than 0.5% of this vault's deposits are currently available to withdraw. You may not be able to exit your full position immediately."

    case VaultWarningType.ORACLE_PRICE_DERIVATION:
      return ORACLE_PRICE_DERIVATION_VAULT_MESSAGE

    case VaultWarningType.ORACLE_UNUSABLE:
      return ORACLE_UNUSABLE_VAULT_MESSAGE

    // V2-only. Market-sourced bad debt never reaches this switch: it returns above.
    case VaultV2WarningType.BAD_DEBT_UNREALIZED:
      return BAD_DEBT_UNREALIZED_VAULT_MESSAGE

    case VaultWarningType.CUSTOM:
      return content

    default:
      return null
  }
}
